/**
 * PATH: packages/pricelist/src/columnSchemaOps.ts
 * FILE_ID: PKG-PRICELIST-COLOPS-001
 * PURPOSE: Pure, immutable helpers to restructure a section's column_schema —
 *          move, remove, and patch a column by index. Used by the Column
 *          Manager, which saves the whole schema back via updateSection.
 * WHY: Operators asked to structure pricelists properly (reorder/edit/remove
 *      columns, not just add). Keys stay stable so item data keyed to them
 *      remains valid; only the schema array (order/label/type/formula) changes.
 * RELATED: ColumnManagerDialog.tsx, usePricelistWorkbook (runColumnOp), types.ts
 */
import type { ColumnDef } from './types'

/** Swap the column at `index` with its neighbour in `dir` (-1 up / +1 down). */
export function moveColumn(schema: ColumnDef[], index: number, dir: -1 | 1): ColumnDef[] {
  const j = index + dir
  if (index < 0 || index >= schema.length || j < 0 || j >= schema.length) return schema
  const next = [...schema]
  ;[next[index], next[j]] = [next[j], next[index]]
  return next
}

/**
 * Move the column at `from` to position `to` (drag-and-drop reorder). Every
 * other column keeps its relative order. Out-of-range indices are a no-op.
 */
export function moveColumnTo(schema: ColumnDef[], from: number, to: number): ColumnDef[] {
  if (
    from === to ||
    from < 0 || from >= schema.length ||
    to < 0 || to >= schema.length
  ) return schema
  const next = [...schema]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** Remove the column at `index` (out-of-range index is a no-op). */
export function removeColumnAt(schema: ColumnDef[], index: number): ColumnDef[] {
  if (index < 0 || index >= schema.length) return schema
  return schema.filter((_, i) => i !== index)
}

/** Shallow-merge `patch` into the column at `index` (key normally kept stable). */
export function updateColumnAt(
  schema: ColumnDef[],
  index: number,
  patch: Partial<ColumnDef>,
): ColumnDef[] {
  if (index < 0 || index >= schema.length) return schema
  return schema.map((c, i) => (i === index ? { ...c, ...patch } : c))
}
